import dayjs from 'dayjs';
import { downloadReportData, getHistoryReport } from 'lib/api';
import { LoadingIndicator } from 'lib/components';
import { reportData } from 'lib/const';
import React from 'react';
import { MdEmail, MdFileDownload, MdKeyboardArrowLeft } from 'react-icons/md';
import { RouteComponentProps, useHistory } from 'react-router';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components/macro';
import { Method } from 'axios';

import { NotFound } from 'app/pages/notFound/NotFound';
import { Gap, HeaderWrapper } from 'lib/components/styles/layout';
import { Heading } from 'lib/components/styles/typography';
import { errorToast } from 'lib/components/toasts/error';
import { STANDARD_DATE_FORMAT } from 'lib/const/DateFormat';
import { downloadFileFromUrl } from 'lib/utils/functions';
import { SendReportModal, timeRangesOptions } from '../../components';
import { Button } from 'react-covideo-common';

const ArrowWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ReportDetails = styled.div`
  padding: 20px 24px;
  border-radius: 4px;
  box-shadow: 0 4px 12px 0 #1d1e240a;
  border: solid 1px #f7f8fa;
`;

const ReportDetailsHeader = styled.div`
  font-size: 18px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  color: #272a32;
`;

const ReportDetailsEmail = styled.div`
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.71;
  letter-spacing: normal;
  color: #4e5461;
`;

const ReportDetailsTimeRange = styled.div`
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: #272a32;
  margin-bottom: 8px;
`;

interface MatchParams {
  historyId: string;
}

const dateFormat = `${STANDARD_DATE_FORMAT} hh:mm A`;
const shortDateFormat = STANDARD_DATE_FORMAT;

export const Details = withRouter((props: RouteComponentProps<MatchParams>) => {
  const [report, setReport] = React.useState({} as any);
  const [loading, setLoading] = React.useState(true);
  const [showSendReportModal, setShowSendReportModal] = React.useState(false);

  const history = useHistory();
  const { historyId } = props.match.params;

  const fetchHistoryReport = async (historyId: string) => {
    setLoading(true);
    try {
      const data = await getHistoryReport({ historyId });
      data.url = '';
      data.fileName = '';
      data.method = 'post' as Method;

      if (!!data.snapshotUrl) {
        data.url = `reports-history/${historyId}`;
        data.fileName = `${data.report}.csv`;
      } else if (data.reportId) {
        data.url = `custom-reports/${data.reportId}`;
        data.fileName = `${data.reportId}.csv`;
        data.method = 'get' as Method;
      } else if (data.report === 'Users') {
        data.url = 'metrics/users';
        data.fileName = 'Users.csv';
      } else if (data.report === 'Companies') {
        data.url = 'metrics/companies';
        data.fileName = 'Companies.csv';
      } else {
        const foundReport = Object.keys(reportData).find(
          key => reportData[key].label === data.report
        );
        if (foundReport) {
          data.url = `${reportData[foundReport].url}`;
          data.fileName = `${data.report}.csv`;
        }
      }
      setReport(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchHistoryReport(historyId);
  }, [historyId]);

  const downloadReport = async () => {
    if (!!report.snapshotUrl) {
      return await downloadFileFromUrl(report.snapshotUrl, report.fileName);
    }

    return downloadReportData(
      `${report.url}/download`,
      report.fileName,
      report.from,
      report.to,
      [],
      '',
      report.method
    );
  };

  const range = timeRangesOptions.find(option => option.value === report.range);

  if (loading) {
    return <LoadingIndicator isLoading={loading} />;
  }

  if (Object.keys(report).length < 1) {
    return <NotFound />;
  }

  return (
    <>
      <HeaderWrapper>
        <Gap gap='2px'>
          <ArrowWrapper onClick={() => history.goBack()}>
            <MdKeyboardArrowLeft size={24} />
          </ArrowWrapper>
          <Heading>Report Details</Heading>
        </Gap>

        <Gap>
          <Button
            text='Send'
            icon={<MdEmail size={20} />}
            onClick={() => setShowSendReportModal(true)}
          />
          <Button
            text='Download'
            icon={<MdFileDownload size={20} />}
            onClick={() => downloadReport()}
          />
        </Gap>
      </HeaderWrapper>
      <ReportDetails>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <ReportDetailsHeader>{report.report}</ReportDetailsHeader>
          <ReportDetailsTimeRange>
            {dayjs(report.dateSent).format(dateFormat)}
          </ReportDetailsTimeRange>
        </div>
        <ReportDetailsTimeRange>
          {range && range.label} ({dayjs(report.from).format(shortDateFormat)} -{' '}
          {dayjs(report.to).format(shortDateFormat)})
        </ReportDetailsTimeRange>
        <ReportDetailsEmail>{report.email}</ReportDetailsEmail>
      </ReportDetails>
      {showSendReportModal && (
        <SendReportModal
          handleModalClose={() => setShowSendReportModal(false)}
          startDate={report.from}
          endDate={report.to}
          range={report.range}
          reportUrl={report.url}
          reportId={report.reportId}
          skipCreateRecurring={!report.reportId ?? true}
          onError={() =>
            errorToast({
              title:
                'There was an error sending your report. Please try again.',
            })
          }
        />
      )}
    </>
  );
});
